/**
 * Converts an AnalysisResult (from the analyzer API) into a Song.
 *
 * Design: the API returns a flat `notes[]` array where each note carries its
 * harmonic context (chordId, symbol, romanNumeral, tone), structural context
 * (sectionId), and hand assignment (hand: 'left' | 'right' | null).
 *
 * This factory builds Parts based on the `hand` field, not the MIDI tracks.
 * Notes with hand='right' form the "Right Hand" Part, hand='left' form the
 * "Left Hand" Part. Notes with hand=null (non-piano tracks) are grouped by
 * their original track index as a fallback.
 */

import type {
  AnalysisResult,
  AnalysisChord,
  AnalysisNote,
  AnalysisPedalEvent,
  AnalysisTrack,
  ApiSongResponse,
} from "./analyzer-types";
import { Note } from "./note";
import { Part, NoteCluster, AnnotatedNote, NoteHarmonyInfo } from "./part";
import { Song, SongAnalysis, SustainChange } from "./song";
import { Instrument } from "../track/instrument";
import type { DetectedKey } from "./key";

const NOTE_NAME_TO_PC: Record<string, number> = {
  C: 0, "C#": 1, Db: 1,
  D: 2, "D#": 3, Eb: 3,
  E: 4, Fb: 4,
  F: 5, "F#": 6, Gb: 6,
  G: 7, "G#": 8, Ab: 8,
  A: 9, "A#": 10, Bb: 10,
  B: 11, Cb: 11,
};

function tonicStringToPitchClass(tonic: string): number {
  return NOTE_NAME_TO_PC[tonic] ?? 0;
}

type PartGroup = {
  groupKey: string;
  name: string;
  role: string;
};

/**
 * Le pédalage du morceau, tiré de ce que la source a écrit.
 *
 * Deux filtres, et rien d'autre :
 *
 * - **la pédale forte seule.** L'analyseur lit aussi la sostenuto (CC 66), qui
 *   ne retient que ce qui était déjà enfoncé, et la douce (CC 67), qui ne
 *   retient rien du tout. Les jouer comme une pédale forte ferait durer des
 *   notes que personne n'a tenues.
 * - **les changements seuls.** Beaucoup de pédales renvoient le CC 64 tant
 *   qu'elles sont enfoncées : le morceau le plus pédalé du catalogue porte
 *   1685 mouvements, dont 1428 répètent l'état courant. Seul compte l'instant
 *   où l'étouffoir bouge, et la lane n'a pas à porter le reste.
 *
 * L'état de départ est « levée » : une première remontée ne dit rien de plus
 * que le silence, et n'entre pas.
 */
export function sustainFromPedals(pedals: readonly AnalysisPedalEvent[] | undefined): SustainChange[] {
  const changes: SustainChange[] = [];
  let down = false;

  for (const pedal of pedals ?? []) {
    if (pedal.kind !== "sustain" || pedal.down === down) {
      continue;
    }

    down = pedal.down;
    changes.push({ tick: pedal.tick, down });
  }

  return changes;
}

/** Convert an API note to its richer in-app annotated form. */
/**
 * Le nom d'une note sans son octave : `Eb5` devient `Eb`, `G##3` devient `G##`.
 *
 * L'octave négative de l'analyseur — `C-1` — est retirée elle aussi, ce que le
 * signe dans le motif suffit à couvrir.
 */
function withoutOctave(name: string): string | null {
  const bare = name.replace(/-?\d+$/, "");

  return bare === "" ? null : bare;
}

function toAnnotatedNote(n: AnalysisNote): AnnotatedNote {
  return {
    tick: n.tick,
    durationTicks: n.durationTicks,
    velocity: n.velocity,
    note: Note.fromMidi(n.midi),
    finger: n.finger,
    spelling: withoutOctave(n.name),
    harmony: n.harmony
      ? {
          chordId: n.harmony.chordId,
          symbol: n.harmony.symbol,
          romanNumeral: n.harmony.romanNumeral,
          tone: n.harmony.tone,
        }
      : null,
    sectionId: n.sectionId,
    hand: n.hand,
  };
}

/**
 * Build a Song from the full API song response (piece + arrangement + score).
 *
 * Parts are built from the `hand` field on each note:
 * - hand='right' → "Right Hand" Part (role 'right-hand')
 * - hand='left'  → "Left Hand" Part (role 'left-hand')
 * - hand=null    → grouped by original track index (non-piano tracks)
 */
export function createSongFromAnalysis(response: ApiSongResponse): Song {
  const { piece, score } = response;
  const result = score.content;
  const { metadata, tempo, key, tracks, notes } = result;
  // L'analyseur publie `chords: null` quand il n'a trouvé aucune harmonie —
  // pas un tableau vide, `null`. Sans cette normalisation, `for (const chord
  // of chords)` jette « chords is not iterable » et le morceau ne s'ouvre pas
  // du tout, alors que l'absence d'harmonie n'empêche rien : les notes, le
  // tempo et les sections sont là, et c'est tout ce qu'il faut pour jouer.
  //
  // Normalisé ici, à l'entrée unique par laquelle passent les deux chemins
  // (catalogue et analyse brute), pour que la nullité ne se propage pas dans
  // les vingt appelants en aval.
  const chords = result.chords ?? [];

  // 1. Create Song
  const song = new Song(metadata.ppq, metadata.totalTicks);
  song.setTitle(piece.title);
  song.setComposer(piece.composer);

  if (result.difficulty) {
    song.setDifficulty(result.difficulty);
  }

  // 2. Populate MusicalTimeline
  const timeline = song.getMusicalTimeline();

  for (const tc of tempo.tempoChanges) {
    timeline.addTempo(tc.tick, tc.bpm);
  }

  for (const ts of tempo.timeSignatures) {
    timeline.addTimeSignature(ts.tick, ts.numerator, ts.denominator);
  }

  // La pédale n'est pas une conversion de temps, mais elle vient de la même
  // carte et se lit à la même échelle : les ticks du morceau.
  song.setSustain(sustainFromPedals(tempo.pedals));

  // 3. Set key
  const detectedKey: DetectedKey = {
    tonic: tonicStringToPitchClass(key.tonic),
    mode: key.mode,
    confidence: key.confidence,
    label: key.label,
    relative: key.relative,
  };
  song.setKey(detectedKey);

  // 4. Build a chord lookup: chordId → AnalysisChord
  const chordById = new Map<string, AnalysisChord>();

  for (const chord of chords) {
    chordById.set(chord.id, chord);
  }

  // 5. Build a track lookup for instrument info
  const trackByIndex = new Map<number, AnalysisTrack>();

  for (const t of tracks) {
    trackByIndex.set(t.index, t);
  }

  // 6. Group notes by hand (or by track for hand=null)
  //    Key: 'right' | 'left' | 'track:<index>' for null-hand notes
  const notesByGroup = new Map<string, AnalysisNote[]>();

  for (const note of notes) {
    const groupKey = note.hand != null ? note.hand : `track:${note.track}`;
    let arr = notesByGroup.get(groupKey);

    if (!arr) {
      arr = [];
      notesByGroup.set(groupKey, arr);
    }

    arr.push(note);
  }

  // 7. Create Parts, right hand first, then left hand, then any remaining track groups
  const partGroups: PartGroup[] = [];

  if (notesByGroup.has("right")) {
    partGroups.push({ groupKey: "right", name: "Right Hand", role: "right-hand" });
  }

  if (notesByGroup.has("left")) {
    partGroups.push({ groupKey: "left", name: "Left Hand", role: "left-hand" });
  }

  // Remaining groups (hand=null, keyed as 'track:<index>')
  for (const groupKey of notesByGroup.keys()) {
    if (groupKey === "right" || groupKey === "left") {
      continue;
    }

    const trackIndex = parseInt(groupKey.split(":")[1], 10);
    const apiTrack = trackByIndex.get(trackIndex);

    partGroups.push({
      groupKey,
      name: apiTrack?.name ?? `Track ${trackIndex}`,
      role: apiTrack?.role ?? "unknown",
    });
  }

  for (const { groupKey, name, role } of partGroups) {
    const groupNotes = notesByGroup.get(groupKey)!;

    // Resolve instrument: pick from the first note's track
    const firstTrack = trackByIndex.get(groupNotes[0].track);
    const instrumentName = firstTrack?.instrument ?? "acoustic_grand_piano";

    const part = new Part(name, new Instrument(instrumentName, instrumentName));
    part.setRole(role);

    for (const n of groupNotes) {
      const annotated = toAnnotatedNote(n);
      part.addNote(
        annotated.tick,
        annotated.durationTicks,
        annotated.velocity,
        annotated.note,
        annotated.finger,
      );
      part.addAnnotatedNote(annotated);
    }

    // Build clusters from chords, collecting notes whose chordId matches
    const clusters = buildClustersForPart(groupNotes, chords, chordById);
    part.setClusters(clusters);

    song.addPart(part);
  }

  // 8. Attach analysis data
  const analysis: SongAnalysis = {
    key,
    chords,
    sections: result.sections,
    // Le découpage au-dessus des sections : les prises d'un enregistrement.
    // Absent de toute analyse venue de l'analyseur, qui n'en fabrique pas.
    runs: result.runs,
    phrases: result.phrases,
    handPlacements: result.handPlacements,
    handGrips: result.handGrips,
    statistics: result.statistics,
  };
  song.setAnalysis(analysis);

  return song;
}

/**
 * Build a Song from a raw AnalysisResult (no piece/arrangement wrapper).
 * Used for generated exercises and history replay.
 */
export function createSongFromRawAnalysis(result: AnalysisResult): Song {
  return createSongFromAnalysis({
    piece: { id: '', title: '', composer: null, genre: null },
    arrangement: { id: '', arranger: null, description: null },
    score: {
      id: '',
      title: null,
      source: 'generated',
      noteCount: result.notes.length,
      measureCount: result.metadata.measures,
      content: result,
    },
    midiFileId: null,
    createdAt: '',
  });
}

// ── Helpers ────────────────────────────────────────────────────

/**
 * Build NoteCluster[] for a Part (hand group) from the chord list.
 *
 * For each chord, collect all notes from this group whose harmony.chordId matches,
 * then build an enriched NoteCluster.
 */
function buildClustersForPart(
  partNotes: AnalysisNote[],
  chords: AnalysisChord[],
  chordById: Map<string, AnalysisChord>,
): NoteCluster[] {
  // chordById is unused for now but kept in the signature for future
  // enrichments (e.g. backfilling chord context from a foreign part).
  void chordById;

  // Index notes by chordId for fast lookup
  const notesByChordId = new Map<string, AnalysisNote[]>();

  for (const n of partNotes) {
    if (!n.harmony) {
      continue;
    }

    let arr = notesByChordId.get(n.harmony.chordId);

    if (!arr) {
      arr = [];
      notesByChordId.set(n.harmony.chordId, arr);
    }

    arr.push(n);
  }

  const clusters: NoteCluster[] = [];

  for (const chord of chords) {
    const chordNotes = notesByChordId.get(chord.id);

    if (!chordNotes || chordNotes.length === 0) {
      continue;
    }

    const annotatedNotes = chordNotes.map(toAnnotatedNote);
    const noteSpans = annotatedNotes.map((a) => ({
      tick: a.tick,
      durationTicks: a.durationTicks,
      velocity: a.velocity,
      note: a.note,
      finger: a.finger,
    }));

    clusters.push({
      startTick: chord.tick,
      endTick: chord.endTick,
      notes: noteSpans,
      annotatedNotes,
      chord: null,
      harmony: chord,
    });
  }

  return clusters;
}
